import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Search, UtensilsCrossed, Dumbbell, Video, Trash2, User } from "lucide-react";

const TABS = [
  { id: "all", label: "Tous les plans", icon: null },
  { id: "diet", label: "Alimentaires", icon: UtensilsCrossed },
  { id: "training", label: "Entraînement", icon: Dumbbell },
  { id: "videos", label: "Vidéos", icon: Video },
];

export default function AdminContent() {
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [plans, setPlans] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const promises = [];

    if (tab !== "videos") {
      const params = new URLSearchParams();
      if (tab === "diet" || tab === "training") params.set("type", tab);
      if (search.trim()) params.set("search", search.trim());
      promises.push(api.get(`/admin/plans?${params}`).then((r) => setPlans(r.data)));
    } else {
      setPlans([]);
    }

    if (tab === "videos" || tab === "all") {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      promises.push(api.get(`/admin/videos?${params}`).then((r) => setVideos(r.data)));
    } else {
      setVideos([]);
    }

    Promise.all(promises)
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [tab, search]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const deletePlan = async (type, planId) => {
    try {
      await api.delete(`/admin/plans/${type}/${planId}`);
      setPlans((prev) => prev.filter((p) => p.plan_id !== planId));
      toast.success("Plan supprimé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const deleteVideo = async (videoId) => {
    try {
      await api.delete(`/admin/videos/${videoId}`);
      setVideos((prev) => prev.filter((v) => v.video_id !== videoId));
      toast.success("Vidéo supprimée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const showPlans = tab !== "videos";
  const showVideos = tab === "videos" || tab === "all";

  return (
    <div>
      <AdminPageHeader
        title="Contenu"
        subtitle="Modération des plans alimentaires, programmes et vidéos"
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-medium border transition-colors ${
              tab === t.id ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
          >
            {t.icon && <t.icon className="w-3.5 h-3.5" />}
            {t.label}
          </button>
        ))}
      </div>

      <div className="relative max-w-sm mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par titre..."
          className="pl-9 h-10 bg-white"
        />
      </div>

      {showPlans && (
        <Card className="border-slate-200 mb-6">
          <CardContent className="p-0">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-900">Plans ({plans.length})</h2>
            </div>
            {loading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
              </div>
            ) : plans.length === 0 ? (
              <p className="text-center py-12 text-slate-400 text-sm">Aucun plan trouvé</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {plans.map((p) => (
                  <div key={p.plan_id} className="flex items-center gap-4 px-6 py-4 hover:bg-slate-50">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                      p.plan_type === "diet" ? "bg-amber-50 text-amber-600" : "bg-rose-50 text-rose-600"
                    }`}>
                      {p.plan_type === "diet" ? <UtensilsCrossed className="w-4 h-4" /> : <Dumbbell className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{p.title}</p>
                      <p className="text-xs text-slate-400">
                        Coach: {p.coach_name} · Élève: {p.student_name}
                      </p>
                    </div>
                    <span className="text-xs text-slate-400 hidden sm:block">
                      {p.created_at ? new Date(p.created_at).toLocaleDateString("fr-FR") : "—"}
                    </span>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer ce plan ?</AlertDialogTitle>
                          <AlertDialogDescription>
                            « {p.title} » sera définitivement supprimé.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deletePlan(p.plan_type, p.plan_id)} className="bg-red-600 hover:bg-red-700">
                            Supprimer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {showVideos && (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-900">Vidéos ({videos.length})</h2>
            </div>
            {loading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
              </div>
            ) : videos.length === 0 ? (
              <p className="text-center py-12 text-slate-400 text-sm">Aucune vidéo trouvée</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {videos.map((v) => (
                  <div key={v.video_id} className="flex items-center gap-4 px-6 py-4 hover:bg-slate-50">
                    <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                      <Video className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{v.title}</p>
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <User className="w-3 h-3" /> {v.coach_name} · {v.video_source}
                      </p>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600">{v.video_source}</span>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer cette vidéo ?</AlertDialogTitle>
                          <AlertDialogDescription>« {v.title} » sera définitivement supprimée.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteVideo(v.video_id)} className="bg-red-600 hover:bg-red-700">
                            Supprimer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}